import { useCallback, useMemo, useState } from 'react'
import { DriversTableData } from '../data/driversTableData'
import { MobileCardData } from '../data/MobileCardData'
import { FormDetailsMap, DriverForm } from './DriverForm'
import { GetMissingMandatoryFields, type MissingField } from './GetMissingMandatoryFields'
import { MessageForm } from '../components/MessageForm'
import { FormProvider, useForm } from 'react-hook-form'
import type { DriverFormValues } from '../data/DriverFormValidation'
import type { Driver } from '../../../../../types'
import { quitDriver } from '../../../../../api/api'
import { employeeAccessEmail } from '../../../../../api/flow'
import { toast } from '../../../../../toast'
import { Stack } from '../../../../cars/components/V3/_shared/Stack'
import { PrimaryButton } from '../../../../cars/components/V3/_shared/Buttons/PrimaryButton'
import { DialogNew } from '../../../../cars/components/V3/_shared/DialogNew'

import GenericTable from '../../../../cars/components/V3/_shared/GenericTable/GenericTable'
import { Search } from '../../Search'

type OpenModal =
  | { type: 'newDriver' }
  | { type: 'edit'; driver: Driver; missingFields: MissingField[] }
  | { type: 'access'; driver: Driver; missingFields: MissingField[] }
  | { type: 'message'; driver: Driver; missingFields: MissingField[] }
  | null

export const DriversTable = ({
  drivers,
  isLoading,
  companyId,
  pattern,
  companyName,
  onSearchChange,
  onEditDriverFinish,
}: {
  drivers: Driver[]
  isLoading: boolean
  companyId?: string
  pattern: string
  companyName: string
  onSearchChange: (value: string) => void
  onEditDriverFinish: () => Promise<void> | void
}) => {
  const [openModal, setOpenModal] = useState<OpenModal>(null)
  const editDriverMethod = useForm<DriverFormValues>({
    defaultValues: FormDetailsMap({}),
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const resetAccessForm = useCallback(() => {
    editDriverMethod.reset(FormDetailsMap({}), {
      keepErrors: false,
      keepDirty: false,
      keepTouched: false,
    })
  }, [editDriverMethod])

  const closeModal = useCallback(() => {
    resetAccessForm()
    setOpenModal(null)
  }, [resetAccessForm])

  const handleNewDriver = useCallback(() => setOpenModal({ type: 'newDriver' }), [])

  const handleEditDriver = useCallback(
    (driver: Driver) => setOpenModal({ type: 'edit', driver, missingFields: GetMissingMandatoryFields(driver) }),
    [],
  )

  const handleEditAccess = useCallback(
    (driver: Driver) => {
      resetAccessForm()
      setOpenModal({ type: 'access', driver, missingFields: GetMissingMandatoryFields(driver) })
    },
    [resetAccessForm],
  )

  const columns = useMemo(
    () =>
      DriversTableData({
        editDriver: handleEditDriver,
        editAccess: handleEditAccess,
      }),
    [handleEditDriver, handleEditAccess],
  )

  const handleDiscontinue = useCallback(
    async (driverId: string) => {
      const ok = window.confirm('Er du sikker på at du vil avslutte denne ansatte?')
      if (!ok) return

      try {
        await quitDriver(driverId)
        closeModal()
        await onEditDriverFinish()
      } catch (err) {
        console.error(err)
        toast.error('Error', 'Kunne ikke avslutte ansatt, prøv igjen eller kontakt administrator')
      }
    },
    [closeModal, onEditDriverFinish],
  )

  const accessRegNo = editDriverMethod.watch('webUser.regNo') ?? ''
  const accessType = editDriverMethod.watch('webUser.access') ?? 'YES'
  const accessMessage = editDriverMethod.watch('webUser.message') ?? ''
  const canSubmitAccess =
    accessMessage.trim().length > 0 && (accessType !== 'OWN_CAR_ONLY' || accessRegNo.trim().length > 0)

  const handleAccessSubmit = useCallback(
    async (data: DriverFormValues) => {
      if (openModal?.type !== 'access') return

      try {
        await employeeAccessEmail({
          company: companyName,
          message: data.webUser?.message,
          access: data.webUser?.access,
          filterAccess: data.webUser?.filterAccess,
          regNo: data.webUser?.regNo,
          name: openModal.driver.name,
        })

        toast.success('Lagret', 'Webtilgang er oppdatert')
        closeModal()
        await onEditDriverFinish()
      } catch (err) {
        console.error(err)
        toast.error('Error', 'Kunne ikke oppdatere webtilgang')
      }
    },
    [openModal, companyName, closeModal, onEditDriverFinish],
  )

  return (
    <Stack spacing="sm">
      <div className="w-full flex gap-4 justify-between items-center">
        <Search value={pattern} onChange={onSearchChange} />
        <div className="flex w-40 items-center">
          <PrimaryButton onClick={handleNewDriver}>Legg til ansatt</PrimaryButton>
        </div>
      </div>

      <DialogNew
        title="Legg til ansatt"
        description="Fyll ut skjema og legg til ny ansatt"
        open={openModal?.type === 'newDriver'}
        onOpenChange={(open) => !open && closeModal()}
      >
        <DriverForm
          companyId={companyId}
          companyName={companyName}
          onFinish={async () => {
            closeModal()
            await onEditDriverFinish()
          }}
        />
      </DialogNew>

      <DialogNew
        title="Rediger ansatt"
        description="De rød feltene må fylles ut for å lagre"
        open={openModal?.type === 'edit'}
        onOpenChange={(open) => !open && closeModal()}
      >
        <DriverForm
          companyId={companyId}
          companyName={companyName}
          driver={openModal?.type === 'edit' ? openModal.driver : undefined}
          onDiscontinue={() => {
            if (openModal?.type === 'edit') {
              handleDiscontinue(openModal.driver.id)
            }
          }}
          onFinish={async () => {
            closeModal()
            await onEditDriverFinish()
          }}
        />
      </DialogNew>

      <DialogNew
        title="Endre webtilgang"
        description="Send en forespørsel om endring av webtilgang"
        open={openModal?.type === 'access'}
        onOpenChange={(open) => !open && closeModal()}
      >
        <FormProvider {...editDriverMethod}>
          <form onSubmit={editDriverMethod.handleSubmit(handleAccessSubmit)}>
            <Stack spacing="sm">
              <MessageForm />
              <div className="flex w-full justify-end mt-4">
                <PrimaryButton
                  buttonType="submit"
                  disabled={editDriverMethod.formState.isSubmitting || !canSubmitAccess}
                >
                  Send forespørsel
                </PrimaryButton>
              </div>
            </Stack>
          </form>
        </FormProvider>
      </DialogNew>

      <GenericTable
        data={drivers || []}
        className="max-h-[60vh]"
        isLoading={isLoading}
        columns={columns}
        columnWidths={{
          name: { width: 150, minWidth: 150, maxWidth: 200 },
          jobTitle: { width: 150, minWidth: 150, maxWidth: 170 },
          webTilgang: { width: 120, minWidth: 120, maxWidth: 120 },
          hasConfiguratorAccess: { width: 120, minWidth: 120, maxWidth: 120 },
          email: { minWidth: 80, maxWidth: 240 },
          actions: { width: 50, minWidth: 50, maxWidth: 50 },
        }}
        defaultColumnWidth={{ width: 150, minWidth: 100, maxWidth: 600 }}
        mobileCardContent={(row) => <MobileCardData row={row} />}
      />
    </Stack>
  )
}
